import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig from '../config/appConfig';

const UpdateAccessLevelDialog = ({
  isOpen,
  onClose,
  selectedUserIds,
  corporateAccount,
  selectedAccountId,
  login_token,
  permissions
}) => {
  const [accessLevel, setAccessLevel] = useState('');
  const [error, setError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const accessLevelInputRef = useRef(null);



  // Updated dialog styles with fixed layout
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '500px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Focus the access level field when dialog opens
  useEffect(() => {
    if (isOpen && accessLevelInputRef.current) {
      setTimeout(() => {
        accessLevelInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setAccessLevel('');
      setError('');
      setShowValidationErrors(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowValidationErrors(true);
    setError('');
    setIsLoading(true);

    // Validation checks
    if (!accessLevel) {
      setError('Access Level is required');
      setIsLoading(false);
      return;
    }

    const numericAccessLevel = parseInt(accessLevel);
    if (isNaN(numericAccessLevel) || numericAccessLevel < 1 || numericAccessLevel > 9) {
      setError('Access Level must be between 1 and 9');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_user_access_level'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_ids: Array.from(selectedUserIds),
          access_level: numericAccessLevel,
          requesting_for_corporate_account: selectedAccountId
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError(`Access level updated successfully for ${data.affected_users || selectedUserIds.size} user(s)`);

        // Delay closing to show success message
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to update access level');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={dialogStyles.header}>
          Update Access Level ({selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''})
        </div>

        <span style={{ display: 'block', textAlign: 'center', color: 'red', fontWeight: 'bold', margin: '10px 0' }}>
          This is the default access level that takes effect if no project specific access level is defined
        </span>

        {/* Scrollable Content */}
        <div style={dialogStyles.content}>
          <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              {/* Selected Users Display */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Selected Users ({selectedUserIds.size}):
                </label>
                <div style={{
                  maxHeight: '100px',
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  {Array.from(selectedUserIds).map(userId => (
                    <div key={userId} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      • {userId}
                    </div>
                  ))}
                </div>
              </div>

              {/* Access Level Input */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  New Access Level (1-9): <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  ref={accessLevelInputRef}
                  type="number"
                  min="1"
                  max="9"
                  step="1"
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  placeholder="Enter access level (1-9)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${showValidationErrors && !accessLevel ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                  required
                />
                {showValidationErrors && !accessLevel && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Access Level is required
                  </span>
                )}

                {/* Access Level Info */}
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '4px'
                }}>
                  <strong>Note:</strong> Access level must be a number between 1 and 9. The meaning of each level is defined by your account settings.
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div style={dialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={() => onClose(false)}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <button
              type="submit"
              onClick={(e) => {
                if (!permissions.canUpdate) {
                  e.preventDefault();
                  setError('You do not have permission to update user access level');
                  return;
                }
                handleSubmit(e);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: !permissions.canUpdate ? '#f3f4f6' : '#C8F4DE',
                color: !permissions.canUpdate ? '#6b7280' : 'black',
                border: !permissions.canUpdate ? '1px solid #d1d5db' : 'none',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginLeft: '-4px',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {!permissions.canUpdate ?
                'No Permission to Update' :
                (isLoading ? 'Updating Access Level...' : 'Update Access Level')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateAccessLevelDialog;